import { useUploadCustomerDocumentMutation } from 'feature/company/redux/company.api'
import { ClientSubtype } from 'feature/company/types/CompanyRegistrationForm'
import { orderFormSpace } from 'feature/productOrder/namespaces/orderFormSpace'
import { useCallback, useEffect, useRef } from 'react'
import { useFormContext } from 'react-hook-form'
import { DocumentsUploadInput } from 'pages/LkmRegistration/components/DocumentsUploadInput'
import { companyRegistrationStorage } from '../storage/companyRegistrationStorage'
import styles from './DocumentsSection.module.scss'

type DocField = {
  type: string
  label: string
}

const LLC_DOCS: DocField[] = [
  { type: 'charter', label: 'Устав *' },
  {
    type: 'doc_confirm_director_appointment',
    label: 'Документ подтверждающий назначение руководителя *',
  },
  {
    type: 'doc_state_registration',
    label: 'Документ о государственной регистрации *',
  },
  { type: 'egrul', label: 'Выписка ЕГРЮЛ *' },
  { type: 'company_profile_card', label: 'Карточка компании *' },
]

const IE_DOCS: DocField[] = [
  { type: 'egrip', label: 'Выписка ЕГРИП *' },
  { type: 'passport_ie', label: 'Паспорт ИП *' },
  { type: 'ie_profile_card', label: 'Карточка ИП * ' },
]

const DOCS_BY_SUBTYPE: Record<ClientSubtype, DocField[]> = {
  llc: LLC_DOCS,
  ie: IE_DOCS,
}

export function DocumentsSection() {
  const { setValue, watch } = useFormContext()
  const [uploadCustomerDocument] = useUploadCustomerDocumentMutation()
  const clientSubtype = (watch('clientSubtype') as ClientSubtype) ?? 'ie'
  const uuidsRef = useRef<Record<string, string[]>>({})
  const savedFilesRef = useRef<Record<string, orderFormSpace.Images[]>>(
    companyRegistrationStorage.loadDocFiles() as Record<
      string,
      orderFormSpace.Images[]
    >,
  )
  const isFirstRender = useRef(true)

  // Restore uuidsRef from persisted metadata on mount
  useEffect(() => {
    Object.entries(savedFilesRef.current).forEach(([type, files]) => {
      uuidsRef.current[type] = files
        .filter(f => f.serverUid)
        .map(f => f.serverUid as string)
    })
  }, [])

  // Clear persisted data only when clientSubtype actually changes (not on initial mount)
  useEffect(() => {
    if (isFirstRender.current) {
      isFirstRender.current = false
      return
    }
    uuidsRef.current = {}
    savedFilesRef.current = {}
    companyRegistrationStorage.clearDocFiles()
    setValue('documentFileUids', [])
    setValue('documentsValid', false)
  }, [clientSubtype, setValue])

  const syncForm = useCallback(
    (subtype: ClientSubtype, uuids: Record<string, string[]>) => {
      const docs = DOCS_BY_SUBTYPE[subtype]
      const all = Object.values(uuids).flat()
      const allUploaded = docs.every(doc => (uuids[doc.type]?.length ?? 0) > 0)
      setValue('documentFileUids', all)
      setValue('documentsValid', allUploaded)
    },
    [setValue],
  )

  const handleDocChange = useCallback(
    (type: string, uuids: string[]) => {
      uuidsRef.current[type] = uuids
      syncForm(clientSubtype, uuidsRef.current)
    },
    [clientSubtype, syncForm],
  )

  const handleFilesChange = useCallback(
    (type: string, files: orderFormSpace.Images[]) => {
      savedFilesRef.current[type] = files
      companyRegistrationStorage.saveDocFiles(
        savedFilesRef.current as Record<string, unknown[]>,
      )
    },
    [],
  )

  const docs = DOCS_BY_SUBTYPE[clientSubtype]

  return (
    <div className={styles.section}>
      <div className={styles.section__header}>
        <h3 className={styles.section__sectionTitle}>Документы</h3>
        <p className={styles.subtitle}>
          Прикрепите файлы документов в формате PDF, DOCX, PNG или JPEG.
          Максимальный размер каждого файла — 1 МБ.
        </p>
      </div>
      <div className={styles.docs}>
        {docs.map(doc => (
          <DocumentsUploadInput
            key={`${clientSubtype}-${doc.type}`}
            label={doc.label}
            initialFiles={savedFilesRef.current[doc.type] ?? []}
            onUploadFile={async file => {
              const res = await uploadCustomerDocument({
                file,
                type: doc.type,
              }).unwrap()
              if (!res.uid)
                throw new Error('Upload failed: missing uid in response')
              return { uid: res.uid, previewUrl: '' }
            }}
            onChange={uuids => handleDocChange(doc.type, uuids)}
            onFilesChange={files => handleFilesChange(doc.type, files)}
          />
        ))}
      </div>
    </div>
  )
}
